/* === NEW FILE: src/pages/admin/AdminLiveCourseForm.js === */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import AdminMediaLibrary from './AdminMediaLibrary'; // Import the media library component

const AdminLiveCourseForm = ({ courseId, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        title: '',
        summary: '',
        price: '0.00',
        duration: '',
        start_datetime: '',
        schedule_summary: '',
        intro_video_url: '',
        learning_outcomes: '',
        meeting_link: '',
        status: 'Upcoming'
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
    const [selectingFor, setSelectingFor] = useState(null); // 'intro_video_url'
    const [s3Prefix, setS3Prefix] = useState('');

    useEffect(() => {
        if (courseId) {
            setIsLoading(true);
            const token = localStorage.getItem('app_token');
            axios.get(`/api/admin/live-courses/${courseId}`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => {
                    const data = res.data;
                    // Format datetime for the input field
                    const formattedDateTime = data.start_datetime ? new Date(data.start_datetime).toISOString().slice(0, 16) : '';
                    setFormData({ ...data, start_datetime: formattedDateTime });
                    setIsLoading(false);
                })
                .catch(err => {
                    console.error(err);
                    alert('Failed to load course data.');
                    setIsLoading(false);
                });
        }
    }, [courseId]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleEditorChange = (field, data) => {
        setFormData(prev => ({ ...prev, [field]: data }));
    };

    const openMediaLibrary = (field, prefix) => {
        setSelectingFor(field);
        setS3Prefix(prefix);
        setIsMediaLibraryOpen(true);
    };

    const handleMediaSelect = (url) => {
        setFormData({ ...formData, [selectingFor]: url });
        setIsMediaLibraryOpen(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Convert local datetime back to a format the backend can use
        const dataToSave = {
            ...formData,
            start_datetime: new Date(formData.start_datetime).toISOString()
        };
        onSave(dataToSave);
    };

    if (isLoading) return <div className="admin-modal-overlay"><div className="admin-modal-content"><p>Loading form...</p></div></div>;

    return (
        <>
            {isMediaLibraryOpen && (
                <AdminMediaLibrary
                    s3Prefix={s3Prefix}
                    onSelect={handleMediaSelect}
                    onCancel={() => setIsMediaLibraryOpen(false)}
                />
            )}
            <div className="admin-modal-overlay">
                <div className="admin-modal-content">
                    <h2>{courseId ? 'Edit Live Course' : 'Add New Live Course'}</h2>
                    <form onSubmit={handleSubmit} className="admin-form">
                        <div className="form-group">
                            <label>Title</label>
                            <input type="text" name="title" value={formData.title} onChange={handleChange} required />
                        </div>

                        <div className="form-group">
                            <label>Start Date and Time</label>
                            <input type="datetime-local" name="start_datetime" value={formData.start_datetime} onChange={handleChange} required />
                        </div>

                        <div className="form-group">
                            <label>Schedule Summary (e.g., "Tues & Thurs at 7 PM")</label>
                            <input type="text" name="schedule_summary" value={formData.schedule_summary} onChange={handleChange} />
                        </div>

                        <div className="form-group">
                            <label>Duration (e.g., "4 Weeks")</label>
                            <input type="text" name="duration" value={formData.duration} onChange={handleChange} />
                        </div>
                        
                        <div className="form-group">
                            <label>Price (₹)</label>
                            <input type="number" name="price" value={formData.price} onChange={handleChange} step="0.01" required />
                        </div>

                        <div className="form-group">
                            <label>Status</label>
                            <select name="status" value={formData.status} onChange={handleChange}>
                                <option value="Upcoming">Upcoming</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Finished">Finished</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Intro Video (Optional)</label>
                            <div className="media-input-group">
                                <input 
                                    type="text" 
                                    name="intro_video_url" 
                                    value={formData.intro_video_url} 
                                    readOnly 
                                    placeholder="Select a video from the library" 
                                />
                                <button 
                                    type="button" 
                                    className="btn-secondary" 
                                    onClick={() => openMediaLibrary('intro_video_url', 'hj1/public/assets/videos/')}
                                >
                                    Select
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Meeting Link (e.g., Zoom, Google Meet)</label>
                            <input type="text" name="meeting_link" value={formData.meeting_link} onChange={handleChange} placeholder="https://..." required />
                        </div>
                        
                        <div className="form-group">
                            <label>Summary</label>
                            <CKEditor editor={ClassicEditor} data={formData.summary} onChange={(e, editor) => handleEditorChange('summary', editor.getData())} />
                        </div>

                        <div className="form-group">
                            <label>Learning Outcomes</label>
                            <CKEditor editor={ClassicEditor} data={formData.learning_outcomes} onChange={(e, editor) => handleEditorChange('learning_outcomes', editor.getData())} />
                        </div>

                        <div className="form-actions">
                            <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
                            <button type="submit" className="btn-primary">Save Course</button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AdminLiveCourseForm;
